import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, TrendingUp, TrendingDown, AlertTriangle,
  Activity, Users, Wifi, Clock, BarChart3, Brain
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PredictiveAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [customerRisks, setCustomerRisks] = useState([]);
  const [networkCongestion, setNetworkCongestion] = useState(null);
  const [latestMetrics, setLatestMetrics] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      
      // Fetch all customer risks
      const risksResponse = await fetch(`${API_URL}/api/predictive-analytics/customers/all-risks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (risksResponse.ok) {
        const risksData = await risksResponse.json();
        setCustomerRisks(risksData.risks || []);
      }
      
      // Fetch network congestion
      const congestionResponse = await fetch(`${API_URL}/api/predictive-analytics/network/congestion`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (congestionResponse.ok) {
        const congestionData = await congestionResponse.json();
        setNetworkCongestion(congestionData.analysis);
      }
      
      // Fetch latest metrics
      const metricsResponse = await fetch(`${API_URL}/api/predictive-analytics/metrics/latest`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (metricsResponse.ok) {
        const metricsData = await metricsResponse.json();
        setLatestMetrics(metricsData.metrics || []);
      }
      
      setLastUpdate(new Date());
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const triggerAnalysis = async () => {
    setAnalyzing(true);
    try {
      await fetchAllAnalytics();
      toast.success('Analytics refreshed successfully!');
    } catch (error) {
      toast.error('Failed to refresh analytics');
    } finally {
      setAnalyzing(false);
    }
  };

  const getRiskBadgeColor = (riskLevel) => {
    const colors = {
      'low': 'bg-green-100 text-green-800 border-green-300',
      'medium': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'high': 'bg-orange-100 text-orange-800 border-orange-300',
      'critical': 'bg-red-100 text-red-800 border-red-300',
      'unknown': 'bg-gray-100 text-gray-600 border-gray-300',
      'error': 'bg-gray-100 text-gray-600 border-gray-300'
    };
    return colors[riskLevel] || colors['unknown'];
  };

  const getRiskIcon = (riskLevel) => {
    if (riskLevel === 'critical' || riskLevel === 'high') {
      return <AlertTriangle className="h-4 w-4" />;
    }
    return <Activity className="h-4 w-4" />;
  };

  const getCongestionColor = (level) => {
    const colors = {
      'low': 'text-green-600',
      'moderate': 'text-yellow-600',
      'high': 'text-orange-600',
      'critical': 'text-red-600'
    };
    return colors[level] || 'text-gray-600';
  };

  // Calculate summary statistics
  const criticalCount = customerRisks.filter(r => r.risk_level === 'critical').length;
  const highCount = customerRisks.filter(r => r.risk_level === 'high').length;
  const mediumCount = customerRisks.filter(r => r.risk_level === 'medium').length;
  const lowCount = customerRisks.filter(r => r.risk_level === 'low').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/monitoring-dashboard')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-3">
                <Brain className="h-8 w-8 text-purple-600" />
                Predictive Network Analytics
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                AI-powered predictions for connectivity issues and network congestion
              </p>
              {lastUpdate && (
                <p className="text-xs text-gray-500 mt-1">
                  Last updated: {lastUpdate.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
          
          <Button
            onClick={triggerAnalysis}
            disabled={analyzing}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${analyzing ? 'animate-spin' : ''}`} />
            {analyzing ? 'Analyzing...' : 'Refresh Analytics'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="max-w-7xl mx-auto flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading AI-powered analytics...</p>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">Critical Risk</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold text-red-600">{criticalCount}</div>
                  <AlertTriangle className="h-8 w-8 text-red-500" />
                </div>
                <p className="text-xs text-gray-600 mt-2">Customers at critical risk</p>
              </CardContent>
            </Card>

            <Card className="border-orange-200 bg-orange-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">High Risk</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold text-orange-600">{highCount}</div>
                  <TrendingDown className="h-8 w-8 text-orange-500" />
                </div>
                <p className="text-xs text-gray-600 mt-2">Customers at high risk</p>
              </CardContent>
            </Card>

            <Card className="border-yellow-200 bg-yellow-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">Medium Risk</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold text-yellow-600">{mediumCount}</div>
                  <Activity className="h-8 w-8 text-yellow-500" />
                </div>
                <p className="text-xs text-gray-600 mt-2">Customers need monitoring</p>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">Low Risk</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold text-green-600">{lowCount}</div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
                <p className="text-xs text-gray-600 mt-2">Customers performing well</p>
              </CardContent>
            </Card>
          </div>

          {/* Network Congestion Analysis */}
          {networkCongestion && (
            <Card className="border-2 border-indigo-200">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <Wifi className="h-5 w-5 text-indigo-600" />
                  Network Congestion Forecast
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-semibold text-gray-600">Congestion Level: </span>
                    <span className={`text-lg font-bold ${getCongestionColor(networkCongestion.congestion_level)}`}>
                      {networkCongestion.congestion_level?.toUpperCase()}
                    </span>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-2">AI Prediction:</h4>
                    <p className="text-gray-700">{networkCongestion.prediction}</p>
                  </div>
                  
                  {networkCongestion.peak_hours_forecast && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 mb-2">Peak Hours Forecast:</h4>
                      <p className="text-gray-700">{networkCongestion.peak_hours_forecast}</p>
                    </div>
                  )}
                  
                  {networkCongestion.scaling_recommendations && networkCongestion.scaling_recommendations.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 mb-2">Scaling Recommendations:</h4>
                      <ul className="list-disc list-inside space-y-1">
                        {networkCongestion.scaling_recommendations.map((rec, idx) => (
                          <li key={idx} className="text-gray-700 text-sm">{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Customer Risk Analysis Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                Customer Risk Analysis ({customerRisks.length} Customers)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Customer</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Risk Level</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Risk Score</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">AI Prediction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerRisks.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-8 text-gray-500">
                          No risk data available. Metrics collection in progress...
                        </td>
                      </tr>
                    ) : (
                      customerRisks
                        .sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0))
                        .map((risk) => (
                          <tr key={risk.customer_id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">{risk.customer_name}</td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getRiskBadgeColor(risk.risk_level)}`}>
                                {getRiskIcon(risk.risk_level)}
                                {risk.risk_level?.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-lg font-bold">{risk.risk_score || 0}</span>
                              <span className="text-sm text-gray-500">/100</span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-700">{risk.prediction || 'No prediction available'}</td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Latest Metrics Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-600" />
                Latest Network Metrics ({latestMetrics.length} Customers Monitored)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="text-sm text-gray-600 mb-1">Average Latency</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {latestMetrics.length > 0 
                      ? (latestMetrics.reduce((sum, m) => sum + (m.latency_ms || 0), 0) / latestMetrics.filter(m => m.latency_ms).length).toFixed(1)
                      : 0} ms
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="text-sm text-gray-600 mb-1">Customers Online</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {latestMetrics.filter(m => m.is_online).length} / {latestMetrics.length}
                  </div>
                </div>
                
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="text-sm text-gray-600 mb-1">Average Health Score</div>
                  <div className="text-2xl font-bold text-green-600">
                    {latestMetrics.length > 0
                      ? (latestMetrics.reduce((sum, m) => sum + (m.health_score || 0), 0) / latestMetrics.length).toFixed(0)
                      : 0}/100
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PredictiveAnalyticsDashboard;
